import {
  forwardRef,
  useImperativeHandle,
  useEffect,
  useMemo,
  useRef,
  useState,
  type CSSProperties,
  type ReactNode,
} from 'react'
import {
  DndContext,
  DragOverlay,
  PointerSensor,
  useDraggable,
  useDroppable,
  useSensor,
  useSensors,
  type DragEndEvent,
  type DragStartEvent,
} from '@dnd-kit/core'
import AddRoundedIcon from '@mui/icons-material/AddRounded'
import ArrowDropDownCircleOutlinedIcon from '@mui/icons-material/ArrowDropDownCircleOutlined'
import CheckBoxOutlinedIcon from '@mui/icons-material/CheckBoxOutlined'
import DeleteOutlineRoundedIcon from '@mui/icons-material/DeleteOutlineRounded'
import DragIndicatorRoundedIcon from '@mui/icons-material/DragIndicatorRounded'
import EventRoundedIcon from '@mui/icons-material/EventRounded'
import EditRoundedIcon from '@mui/icons-material/EditRounded'
import NotesRoundedIcon from '@mui/icons-material/NotesRounded'
import NumbersRoundedIcon from '@mui/icons-material/NumbersRounded'
import PersonRoundedIcon from '@mui/icons-material/PersonRounded'
import SaveRoundedIcon from '@mui/icons-material/SaveRounded'
import SendRoundedIcon from '@mui/icons-material/SendRounded'
import ShortTextRoundedIcon from '@mui/icons-material/ShortTextRounded'
import UploadFileRoundedIcon from '@mui/icons-material/UploadFileRounded'
import { useAuth } from '../../context/useAuth'
import { useDisplayName } from '../../hooks/useDisplayName'
import { useToast } from '../../context/useToast'
import { useWorkspace } from '../../context/useWorkspace'
import { useNls } from '../../nls/nlsProvider'
import { saveFormDefinition } from '../../services/formsService'
import {
  createFormField,
  buildTitlePrefixes,
  normalizeFormTitle,
  validateFormDefinitionDraft,
  type DropdownFormField,
  type FormDefinition,
  type FormDefinitionDraft,
  type FormField,
  type FormFieldType,
  type FormLogicAction,
  type FormLogicOperator,
  type FormLogicRule,
  type FormTemplate,
  type PersonFieldPickScope,
} from '../../types/forms'
import { Button, Checkbox, DatePicker, IconButton, Pressable, Select, TextInput } from '../Common'
import { getFormsFieldTypeName, getFormsFieldTypeLabel, getFormsLifecycleStatusLabel, type FormsNlsT } from './FormsNls'
import { hydrateBuilderDraft } from './formBuilderUtils'
import { FormRenderer } from './FormRenderer'

export type ToolboxKind = FormFieldType | 'PARAGRAPH'
const TOOLBOX: Array<{ type: ToolboxKind; icon: ReactNode }> = [
  { type: 'TEXT', icon: <ShortTextRoundedIcon /> },
  { type: 'PARAGRAPH', icon: <NotesRoundedIcon /> },
  { type: 'NUMBER', icon: <NumbersRoundedIcon /> },
  { type: 'DATE', icon: <EventRoundedIcon /> },
  { type: 'DROPDOWN', icon: <ArrowDropDownCircleOutlinedIcon /> },
  { type: 'CHECKBOX', icon: <CheckBoxOutlinedIcon /> },
  { type: 'FILE_UPLOAD', icon: <UploadFileRoundedIcon /> },
  { type: 'PERSON', icon: <PersonRoundedIcon /> },
]

export type FormBuilderHandle = {
  save: () => Promise<boolean>
  addField: (kind?: ToolboxKind) => void
  deleteField: (fieldId: string) => void
}

type FormBuilderProps = {
  sourceForm?: FormDefinition | null
  template?: FormTemplate | null
  persistMode?: 'firestore' | 'callback'
  onClose: () => void
  onSaved: (formId: string) => void
  onDirtyChange: (dirty: boolean) => void
  onDefinitionChange?: (definition: FormDefinition) => void
  canRequestMembers?: boolean
  onRequestMembers?: () => void
}

const optionValueFromLabel = (label: string, fallback: string) => {
  const normalized = label.trim().toLocaleLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/^-|-$/g, '')
  return normalized || fallback
}

export const FormBuilder = forwardRef<FormBuilderHandle, FormBuilderProps>(function FormBuilder({ sourceForm, template, persistMode = 'firestore', onSaved, onDirtyChange, onDefinitionChange, canRequestMembers, onRequestMembers }, ref) {
  const { activeWorkspaceId } = useWorkspace()
  const { user, userProfile } = useAuth()
  const currentDisplayName = useDisplayName(userProfile ?? user)
  const { showToast } = useToast()
  const { t } = useNls()
  const initial = useMemo(() => hydrateBuilderDraft(activeWorkspaceId, user?.uid ?? '', sourceForm, template), [activeWorkspaceId, sourceForm, template, user?.uid])
  const [title, setTitle] = useState(initial.title)
  const [description, setDescription] = useState(initial.description)
  const [fields, setFields] = useState<FormField[]>(initial.fields)
  const [logicRules, setLogicRules] = useState<FormLogicRule[]>(initial.logic.rules)
  const [lifecycleStatus, setLifecycleStatus] = useState(initial.lifecycleStatus)
  const [selectedFieldId, setSelectedFieldId] = useState<string | null>(initial.fields[0]?.id ?? null)
  const [activeDragId, setActiveDragId] = useState<string | null>(null)
  const [saving, setSaving] = useState(false)
  const sensors = useSensors(useSensor(PointerSensor, { activationConstraint: { distance: 5 } }))
  const selectedField = fields.find((field) => field.id === selectedFieldId) ?? null

  const markDirty = () => onDirtyChange(true)
  const updateFields = (updater: (current: FormField[]) => FormField[]) => {
    setFields((current) => updater(current).map((field, index) => ({ ...field, order: index })))
    markDirty()
  }
  const buildDraft = (): FormDefinitionDraft => ({
    ...initial,
    workspaceId: sourceForm?.workspaceId ?? activeWorkspaceId,
    createdBy: sourceForm?.createdBy ?? user?.uid ?? '',
    title,
    description,
    fields: fields.map((field, index) => ({ ...field, order: index })),
    logic: { version: 1, rules: logicRules },
    lifecycleStatus,
  })
  const previewDefinition = useMemo<FormDefinition>(() => {
    const draft = buildDraft()
    return {
      ...draft,
      titleSearch: normalizeFormTitle(draft.title),
      titlePrefixes: buildTitlePrefixes(draft.title),
      submissionCount: sourceForm?.submissionCount ?? draft.submissionCount ?? 0,
      createdAt: sourceForm?.createdAt ?? null,
      updatedAt: sourceForm?.updatedAt ?? null,
    }
  }, [activeWorkspaceId, description, fields, initial, lifecycleStatus, logicRules, sourceForm, title, user?.uid])

  useEffect(() => {
    onDefinitionChange?.(previewDefinition)
  }, [onDefinitionChange, previewDefinition])

  const save = async () => {
    if (persistMode === 'callback') {
      onDefinitionChange?.(previewDefinition)
      onDirtyChange(false)
      return true
    }
    if (!sourceForm && !activeWorkspaceId) { showToast('Choose a workspace before saving this form.', 'error'); return false }
    if (!sourceForm && !user?.uid) { showToast('Sign in to save this form.', 'error'); return false }
    const draft = buildDraft()
    const errors = validateFormDefinitionDraft(draft)
    if (errors.length > 0) { showToast(errors[0], 'error'); return false }
    if (!user) { showToast('Sign in to save this form.', 'error'); return false }
    setSaving(true)
    try {
      await saveFormDefinition(draft, { id: user.uid, name: currentDisplayName || user.email || 'Workspace member' })
      onDirtyChange(false)
      showToast(t('NLSFormsToastSaved'), 'success')
      onSaved(draft.id)
      return true
    } catch (error) {
      showToast(error instanceof Error ? error.message : 'Could not save form.', 'error')
      return false
    } finally { setSaving(false) }
  }
  const addToolField = (kind: ToolboxKind = 'TEXT', index: number = fields.length) => {
    const type: FormFieldType = kind === 'PARAGRAPH' ? 'TEXT' : kind
    const targetIndex = index < 0 ? fields.length : Math.min(index, fields.length)
    const field = createFormField(type, Math.max(0, targetIndex), kind === 'PARAGRAPH')
    updateFields((current) => {
      const next = [...current]
      next.splice(targetIndex < 0 ? current.length : targetIndex, 0, field)
      return next
    })
    setSelectedFieldId(field.id)
    return field
  }

  const deleteField = (fieldId: string) => {
    updateFields((current) => current.filter((field) => field.id !== fieldId))
    setSelectedFieldId((current) => current === fieldId ? null : current)
  }

  useImperativeHandle(ref, () => ({
    save,
    addField: (kind: ToolboxKind = 'TEXT') => {
      addToolField(kind)
    },
    deleteField: (fieldId: string) => {
      deleteField(fieldId)
    },
  }))

  const dragOccurredRef = useRef(false)
  const handleDragStart = (event: DragStartEvent) => {
    dragOccurredRef.current = true
    setActiveDragId(String(event.active.id))
  }
  const handleDragEnd = (event: DragEndEvent) => {
    dragOccurredRef.current = true
    setTimeout(() => {
      dragOccurredRef.current = false
    }, 150)
    const activeId = String(event.active.id)
    const overId = event.over ? String(event.over.id) : null
    setActiveDragId(null)
    if (!overId) return
    const targetIndex = overId === 'form-canvas' ? fields.length : fields.findIndex((field) => `field:${field.id}` === overId)
    if (activeId.startsWith('tool:')) {
      const kind = activeId.slice(5) as ToolboxKind
      addToolField(kind, targetIndex)
      return
    }
    if (!activeId.startsWith('field:')) return
    const fieldId = activeId.slice(6)
    const fromIndex = fields.findIndex((field) => field.id === fieldId)
    if (fromIndex < 0) return
    const destination = targetIndex < 0 ? fields.length - 1 : targetIndex > fromIndex ? targetIndex - 1 : targetIndex
    updateFields((current) => { const next = [...current]; const [moved] = next.splice(fromIndex, 1); next.splice(destination, 0, moved); return next })
  }

  const handleToolboxAdd = (type: ToolboxKind) => {
    if (dragOccurredRef.current || activeDragId) return
    addToolField(type)
  }

  const updateSelected = (patch: Partial<FormField>) => updateFields((current) => current.map((field) => field.id === selectedFieldId ? ({ ...field, ...patch } as FormField) : field))
  const updateValidation = (key: string, value: string | number | undefined) => updateFields((current) => current.map((field) => field.id === selectedFieldId ? ({ ...field, validation: { ...field.validation, [key]: value } } as FormField) : field))
  const selectedRule = logicRules.find((rule) => rule.then.targetFieldId === selectedFieldId) ?? null
  const previousFields = selectedField ? fields.slice(0, selectedField.order) : []
  const updateLogic = (patch: Partial<FormLogicRule>) => {
    if (!selectedField || previousFields.length === 0) return
    const fallback: FormLogicRule = { id: crypto.randomUUID(), when: { fieldId: previousFields[0].id, operator: 'EQUALS', value: '' }, then: { action: 'SHOW', targetFieldId: selectedField.id } }
    const next = { ...(selectedRule ?? fallback), ...patch, when: { ...(selectedRule ?? fallback).when, ...(patch.when ?? {}) }, then: { ...(selectedRule ?? fallback).then, ...(patch.then ?? {}) } }
    setLogicRules((current) => [...current.filter((rule) => rule.then.targetFieldId !== selectedFieldId), next])
    markDirty()
  }

  return (
    <section className="formBuilder formBuilder--operational" aria-label="Visual form builder">
      <header className="formBuilderHeader">
        <div className="formBuilderHeaderCopy">
          <label className="formBuilderTitleField">
            <TextInput className="formBuilderTitleInput" value={title} onChange={(next) => { setTitle(next); markDirty() }} aria-label="Form title" placeholder="Untitled form" />
            <EditRoundedIcon aria-hidden="true" />
          </label>
          <label className="formBuilderDescriptionField">
            <TextInput className="formBuilderDescriptionInput" value={description} onChange={(next) => { setDescription(next); markDirty() }} placeholder="Describe this intake workflow" aria-label="Form description" />
            <EditRoundedIcon aria-hidden="true" />
          </label>
        </div>
        <div className="formBuilderHeaderActions">
          <Select<FormDefinitionDraft['lifecycleStatus']> trigger="labeledPill" label={t('NLSFormsLabelStatus')} ariaLabel="Form lifecycle" value={lifecycleStatus} options={[{ value: 'DRAFT', label: getFormsLifecycleStatusLabel('DRAFT', t) }, { value: 'ACTIVE', label: getFormsLifecycleStatusLabel('ACTIVE', t) }, ...(sourceForm?.lifecycleStatus === 'ARCHIVED' ? [{ value: 'ARCHIVED' as const, label: getFormsLifecycleStatusLabel('ARCHIVED', t) }] : [])]} onChange={(value) => { setLifecycleStatus(value); markDirty() }} />
          {canRequestMembers && onRequestMembers && (
            <Button variant="secondary" icon={<SendRoundedIcon />} onClick={onRequestMembers}>{t('NLSFormsActionRequestMembers')}</Button>
          )}
          <Button variant="primary" icon={<SaveRoundedIcon />} disabled={saving} onClick={() => void save()}>{t('NLSFormsActionSaveForm')}</Button>
        </div>
      </header>
      <DndContext sensors={sensors} onDragStart={handleDragStart} onDragEnd={handleDragEnd}>
        <div className="formBuilderWorkspace">
          <aside className="formBuilderColumn formBuilderColumn--toolbox" aria-label="Toolbox">
            <span className="formsEyebrow">Toolbox</span>
            <div className="formBuilderToolboxItems">
              {TOOLBOX.map((tool) => (
                <ToolboxItem
                  key={tool.type}
                  {...tool}
                  label={tool.type === 'PARAGRAPH' ? t('NLSFormsFieldTypeParagraph') : getFormsFieldTypeName(tool.type, t)}
                  onAdd={handleToolboxAdd}
                />
              ))}
            </div>
          </aside>
          <section data-orbit-target="section-header" data-section-id="builder-fields" data-orbit-title="Field list" className="formBuilderColumn formBuilderColumn--canvas" aria-label="Field list">
            <span className="formsEyebrow">Fields</span>
            <BuilderCanvas fields={fields} selectedFieldId={selectedFieldId} t={t} onSelect={setSelectedFieldId} onDelete={deleteField} />
          </section>
          <section className="formBuilderColumn formBuilderColumn--config" aria-label="Configuration">
            <FieldConfiguration field={selectedField} previousFields={previousFields} rule={selectedRule} t={t} onPatch={updateSelected} onValidation={updateValidation} onLogic={updateLogic} onRemoveLogic={() => { setLogicRules((current) => current.filter((rule) => rule.then.targetFieldId !== selectedFieldId)); markDirty() }} />
          </section>
          <section className="formBuilderColumn formBuilderColumn--preview" aria-label="Live preview">
            <span className="formsEyebrow">Live preview</span>
            <div className="formBuilderPreview">
              <FormRenderer definition={previewDefinition} disabled />
            </div>
          </section>
        </div>
        <DragOverlay>{activeDragId ? <div className="formBuilderDragOverlay"><DragIndicatorRoundedIcon /> Moving field</div> : null}</DragOverlay>
      </DndContext>
    </section>
  )
})

const ToolboxItem = ({
  type,
  label,
  icon,
  onAdd,
}: {
  type: ToolboxKind
  label: string
  icon: ReactNode
  onAdd: (type: ToolboxKind) => void
}) => {
  const { attributes, listeners, setNodeRef, transform, isDragging } = useDraggable({ id: `tool:${type}` })
  const style: CSSProperties | undefined = transform ? { transform: `translate3d(${transform.x}px, ${transform.y}px, 0)` } : undefined
  const pointerStartRef = useRef<{ x: number; y: number } | null>(null)

  return (
    <Pressable
      ref={setNodeRef}
      style={style}
      className={isDragging ? 'isDragging' : ''}
      {...attributes}
      {...listeners}
      onPointerDown={(event) => {
        pointerStartRef.current = { x: event.clientX, y: event.clientY }
        listeners?.onPointerDown?.(event)
      }}
      onClick={(event) => {
        if (pointerStartRef.current) {
          const dx = event.clientX - pointerStartRef.current.x
          const dy = event.clientY - pointerStartRef.current.y
          const distance = Math.hypot(dx, dy)
          if (distance > 5) {
            return
          }
        }
        onAdd(type)
      }}
    >
      {icon}
      <span>{label}</span>
      <DragIndicatorRoundedIcon />
    </Pressable>
  )
}

const BuilderCanvas = ({ fields, selectedFieldId, t, onSelect, onDelete }: { fields: FormField[]; selectedFieldId: string | null; t: FormsNlsT; onSelect: (id: string) => void; onDelete: (id: string) => void }) => {
  const { setNodeRef, isOver } = useDroppable({ id: 'form-canvas' })
  return <div ref={setNodeRef} data-orbit-target="canvas" className={`formBuilderCanvas${isOver ? ' isOver' : ''}`}>{fields.length === 0 ? <div className="formBuilderDropPrompt"><AddRoundedIcon /><strong>Drop fields here</strong><span>Build a validated intake flow from the toolbox.</span></div> : fields.map((field) => <BuilderField key={field.id} field={field} t={t} selected={field.id === selectedFieldId} onSelect={() => onSelect(field.id)} onDelete={() => onDelete(field.id)} />)}</div>
}

const BuilderField = ({ field, t, selected, onSelect, onDelete }: { field: FormField; t: FormsNlsT; selected: boolean; onSelect: () => void; onDelete: () => void }) => {
  const id = `field:${field.id}`
  const draggable = useDraggable({ id })
  const droppable = useDroppable({ id })
  const style: CSSProperties | undefined = draggable.transform ? { transform: `translate3d(${draggable.transform.x}px, ${draggable.transform.y}px, 0)` } : undefined
  return <article ref={(node) => { draggable.setNodeRef(node); droppable.setNodeRef(node) }} data-orbit-target="field" data-field-id={field.id} data-orbit-title={field.label} style={style} className={`formBuilderField${selected ? ' isSelected' : ''}${droppable.isOver ? ' isOver' : ''}${draggable.isDragging ? ' isDragging' : ''}`} onClick={onSelect}><Pressable className="formBuilderFieldHandle" aria-label={`Move ${field.label}`} {...draggable.attributes} {...draggable.listeners} onClick={(event) => event.stopPropagation()}><DragIndicatorRoundedIcon /></Pressable><span><strong>{field.label}</strong><small>{getFormsFieldTypeLabel(field, t)}</small></span>{field.required && <em>{t('NLSFormsLabelRequired')}</em>}<IconButton variant="tertiary" size="sm" tone="danger" className="formBuilderFieldDelete" label={`Delete ${field.label}`} onClick={(event) => { event.stopPropagation(); onDelete() }} icon={<DeleteOutlineRoundedIcon />} /></article>
}

const FieldConfiguration = ({ field, previousFields, rule, t, onPatch, onValidation, onLogic, onRemoveLogic }: { field: FormField | null; previousFields: FormField[]; rule: FormLogicRule | null; t: FormsNlsT; onPatch: (patch: Partial<FormField>) => void; onValidation: (key: string, value: string | number | undefined) => void; onLogic: (patch: Partial<FormLogicRule>) => void; onRemoveLogic: () => void }) => (
  <aside className="formFieldConfiguration bg-[var(--surface-card)]" aria-label="Field configuration">
    <span className="formsEyebrow">Configuration</span>
    {!field ? <div className="formConfigEmpty"><strong>Select a field</strong><span>Configure copy, validation, and conditional logic.</span></div> : <>
      <label><span>Label</span><TextInput value={field.label} onChange={(next) => onPatch({ label: next })} /></label>
      <label><span>Description</span><TextInput value={field.description ?? ''} onChange={(next) => onPatch({ description: next || undefined })} /></label>
      {'placeholder' in field && <label><span>Placeholder</span><TextInput value={field.placeholder ?? ''} onChange={(next) => onPatch({ placeholder: next } as Partial<FormField>)} /></label>}
      <Checkbox wrapperClassName="formToggle" checked={field.required} onChange={(next) => onPatch({ required: next })} label={<span><strong>Required</strong><small>Block empty submissions</small></span>} />
      <NumberSetting label="Step index" value={field.step ?? 0} onChange={(value) => onPatch({ step: Math.max(0, Math.floor(value ?? 0)) })} />
      {field.type === 'DROPDOWN' && <label><span>Presentation</span><Select<'SELECT' | 'RADIO'> trigger="pill" ariaLabel="Dropdown presentation" value={field.presentation ?? 'SELECT'} options={[{ value: 'SELECT', label: 'Select menu' }, { value: 'RADIO', label: 'Radio group' }]} onChange={(value) => onPatch({ presentation: value } as Partial<FormField>)} /></label>}
      {field.type === 'DROPDOWN' && <DropdownOptionsEditor field={field} onPatch={onPatch} />}
      {field.type === 'PERSON' && (
        <>
          <Checkbox wrapperClassName="formToggle" checked={Boolean(field.allowMultiple)} onChange={(next) => onPatch({ allowMultiple: next } as Partial<FormField>)} label={<span><strong>Allow multiple</strong><small>Select multiple members</small></span>} />
          <label>
            <span>{t('NLSFormsPersonPickScopeLabel')}</span>
            <Select<PersonFieldPickScope>
              trigger="pill"
              ariaLabel={t('NLSFormsPersonPickScopeLabel')}
              value={field.pickScope ?? 'MEMBERS_AND_HELPERS'}
              options={[
                { value: 'MEMBERS_AND_HELPERS', label: t('NLSFormsPersonPickScopeMembersAndHelpers') },
                { value: 'MEMBERS', label: t('NLSFormsPersonPickScopeMembers') },
              ]}
              onChange={(value) => onPatch({ pickScope: value } as Partial<FormField>)}
            />
          </label>
        </>
      )}
      <fieldset><legend>Validation</legend>{field.type === 'TEXT' && <><NumberSetting label="Min length" value={field.validation.minLength} onChange={(value) => onValidation('minLength', value)} /><NumberSetting label="Max length" value={field.validation.maxLength} onChange={(value) => onValidation('maxLength', value)} /><label><span>Regular expression</span><TextInput value={field.validation.pattern ?? ''} onChange={(next) => onValidation('pattern', next || undefined)} placeholder="^[A-Za-z]+$" /></label></>}{field.type === 'NUMBER' && <><NumberSetting label="Minimum" value={field.validation.min} onChange={(value) => onValidation('min', value)} /><NumberSetting label="Maximum" value={field.validation.max} onChange={(value) => onValidation('max', value)} /><NumberSetting label="Step" value={field.validation.step} onChange={(value) => onValidation('step', value)} /></>}{field.type === 'DATE' && <><label><span>Earliest date</span><DatePicker value={field.validation.minDate ?? ''} onChange={(raw) => onValidation('minDate', raw || undefined)} ariaLabel="Earliest date" /></label><label><span>Latest date</span><DatePicker value={field.validation.maxDate ?? ''} onChange={(raw) => onValidation('maxDate', raw || undefined)} ariaLabel="Latest date" /></label></>}{field.type === 'FILE_UPLOAD' && <><NumberSetting label="Maximum files" value={field.validation.maxFiles} onChange={(value) => onValidation('maxFiles', value ?? 1)} /><NumberSetting label="Max size (MB)" value={Math.round(field.validation.maxFileSizeBytes / 1024 / 1024)} onChange={(value) => onValidation('maxFileSizeBytes', (value ?? 10) * 1024 * 1024)} /></>}{field.type === 'PERSON' && <><NumberSetting label="Min selections" value={field.validation.minSelections} onChange={(value) => onValidation('minSelections', value)} /><NumberSetting label="Max selections" value={field.validation.maxSelections} onChange={(value) => onValidation('maxSelections', value)} /></>}{field.type === 'CHECKBOX' && <small>No additional validation rules.</small>}</fieldset>
      <fieldset><legend>Conditional display</legend>{previousFields.length === 0 ? <small>Add this field after another field to create logic.</small> : <><label><span>Previous field</span><Select trigger="pill" ariaLabel="Previous field for condition" value={rule?.when.fieldId ?? previousFields[0].id} options={previousFields.map((candidate) => ({ value: candidate.id, label: candidate.label }))} onChange={(value) => onLogic({ when: { ...(rule?.when ?? { operator: 'EQUALS', value: '' }), fieldId: value } })} /></label><label><span>Condition</span><Select<FormLogicOperator> trigger="pill" ariaLabel="Condition operator" value={rule?.when.operator ?? 'EQUALS'} options={[{ value: 'EQUALS', label: 'Equals' }, { value: 'NOT_EQUALS', label: 'Does not equal' }, { value: 'CONTAINS', label: 'Contains' }, { value: 'GREATER_THAN', label: 'Greater than' }, { value: 'LESS_THAN', label: 'Less than' }]} onChange={(value) => onLogic({ when: { ...(rule?.when ?? { fieldId: previousFields[0].id, value: '' }), operator: value } })} /></label><label><span>Value</span><TextInput value={String(rule?.when.value ?? '')} onChange={(next) => onLogic({ when: { ...(rule?.when ?? { fieldId: previousFields[0].id, operator: 'EQUALS' }), value: next } })} /></label><label><span>Then</span><Select<FormLogicAction> trigger="pill" ariaLabel="Condition action" value={rule?.then.action ?? 'SHOW'} options={[{ value: 'SHOW', label: 'Show field' }, { value: 'HIDE', label: 'Hide field' }, { value: 'REQUIRE', label: 'Require field' }, { value: 'SKIP_TO', label: 'Skip to field' }]} onChange={(value) => onLogic({ then: { targetFieldId: field.id, action: value } })} /></label>{rule && <Button variant="tertiary" onClick={onRemoveLogic}>Remove condition</Button>}</>}</fieldset>
    </>}
  </aside>
)

const DropdownOptionsEditor = ({ field, onPatch }: { field: DropdownFormField; onPatch: (patch: Partial<FormField>) => void }) => {
  const updateOption = (optionId: string, patch: Partial<DropdownFormField['options'][number]>) => {
    onPatch({ options: field.options.map((option) => option.id === optionId ? { ...option, ...patch } : option) } as Partial<FormField>)
  }
  const addOption = () => {
    const label = `Option ${field.options.length + 1}`
    onPatch({ options: [...field.options, { id: crypto.randomUUID(), label, value: optionValueFromLabel(label, `option-${field.options.length + 1}`) }] } as Partial<FormField>)
  }
  const removeOption = (optionId: string) => {
    if (field.options.length <= 1) return
    onPatch({ options: field.options.filter((option) => option.id !== optionId) } as Partial<FormField>)
  }
  return (
    <fieldset className="formOptionEditor">
      <legend>Options</legend>
      {field.options.map((option, index) => (
        <div key={option.id} className="formOptionRow">
          <label><span>Label</span><TextInput value={option.label} onChange={(next) => {
            const label = next
            updateOption(option.id, { label, value: optionValueFromLabel(label, `option-${index + 1}`) })
          }} /></label>
          <label><span>Value</span><TextInput value={option.value} onChange={(next) => updateOption(option.id, { value: next })} /></label>
          <IconButton
            variant="tertiary"
            size="sm"
            tone="danger"
            label={`Remove ${option.label}`}
            disabled={field.options.length <= 1}
            onClick={() => removeOption(option.id)}
            icon={<DeleteOutlineRoundedIcon />}
          />
        </div>
      ))}
      <Button variant="tertiary" icon={<AddRoundedIcon />} onClick={addOption}>Add option</Button>
    </fieldset>
  )
}

const NumberSetting = ({ label, value, onChange }: { label: string; value: number | undefined; onChange: (value: number | undefined) => void }) => <label><span>{label}</span><TextInput type="number" value={value != null ? String(value) : ''} onChange={(next) => {
  if (next === '') { onChange(undefined); return }
  const num = Number(next)
  onChange(Number.isFinite(num) ? num : undefined)
}} /></label>
